import type { PrioridadeTriagem } from '../../didatico/ocupantes'
import { podeClassificarVitima } from './avaliacaoInicial'
import { ACOES_POR_ID, FASES_ROTEIRO } from './catalogoAcoes'
import { MARCOS_UNICOS, ROTULOS_MARCO } from './acionamento'
import {
  ESTADO_VITIMA_VAZIO,
  ROTULOS_ESTADO_PROBLEMA,
  ROTULOS_REGIAO,
  ROTULOS_RESULTADO_EFEITO,
  achadoAtualDaVitima,
  avaliarIntervencao,
} from './efeitosClinicos'
import type {
  AjusteManualProblema,
  AcaoEquipeAtendimento,
  AvaliacaoCenaTabletop,
  GrauEncarceramento,
  EstadoProblemaClinico,
  EventoHistoricoAtendimento,
  FaseAtendimento,
  FichaVitimaAtendimento,
  IdAcaoAvaliacao,
  InterpretacaoLetra,
  LetraXabcde,
  LeituraRegistrada,
  MotivoAjusteManual,
  NivelAtendimento,
  RegiaoAnatomicaVitima,
  RegistroIntervencaoAtendimento,
  SessaoAtendimentoTabletop,
  TipoMarcoOcorrencia,
  TipoRotaExtracao,
} from './tipos'

function novoId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `evt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export function criarSessaoAtendimento(focoVitimaId: string | null = null): SessaoAtendimentoTabletop {
  return {
    id: novoId(),
    tempoSimuladoSegundos: 0,
    cronometroRodando: false,
    modoExibicao: 'aluno',
    fase: 'abordagem',
    nivel: 'guiado',
    focoVitimaId,
    destaqueRegiaoAnatomica: null,
    estadosVitimas: {},
    vitimasIdentificadas: [],
    avaliacaoCena: {
      seguranca: 'nao_avaliada',
      quantidadeVitimas: null,
      vitimasEjetadas: null,
      itensVerificados: [],
    },
    luvasAPHConfirmadas: false,
    marcos: [],
    riscos: {},
    operacoes: [],
    historicoEventos: [],
  }
}

function assegurarEstadoVitima(sessao: SessaoAtendimentoTabletop, idVitima: string) {
  return sessao.estadosVitimas[idVitima] ?? ESTADO_VITIMA_VAZIO
}

/**
 * Registra que a equipe avaliou um exame: grava uma LEITURA com o horário e
 * o que a vítima apresenta agora. Chamar de novo é uma reavaliação — a
 * leitura anterior fica preservada. Não mostra nada na projeção: isso é
 * `alternarLiberacaoNaTela`. Consultar a ficha no modo instrutor não passa
 * por aqui.
 */
export function revelarAcao(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
  idAcao: IdAcaoAvaliacao,
  ficha: FichaVitimaAtendimento,
): SessaoAtendimentoTabletop {
  const estadoAtual = assegurarEstadoVitima(sessao, idVitima)
  const jaRevelado = estadoAtual.acoesReveladas.includes(idAcao)
  const achado = achadoAtualDaVitima(ficha, idAcao, estadoAtual)
  const acaoDef = ACOES_POR_ID[idAcao]

  const acoesReveladas = jaRevelado
    ? estadoAtual.acoesReveladas
    : [...estadoAtual.acoesReveladas, idAcao]

  const leitura: LeituraRegistrada = {
    id: novoId(),
    idAcao,
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    achado,
    tipo: jaRevelado ? 'reavaliacao' : 'avaliacao',
  }

  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: jaRevelado ? 'reavaliacao' : 'revelacao',
    objetoVitimaId: idVitima,
    descricao: `${jaRevelado ? 'Reavaliação — ' : ''}${acaoDef?.rotulo ?? idAcao}: ${achado.valor}`,
    detalhes: achado.detalhe,
  }

  return {
    ...sessao,
    destaqueRegiaoAnatomica: achado.regiao ?? acaoDef?.regiaoPadrao ?? null,
    estadosVitimas: {
      ...sessao.estadosVitimas,
      [idVitima]: {
        ...estadoAtual,
        acoesReveladas,
        leituras: [...estadoAtual.leituras, leitura],
      },
    },
    historicoEventos: [novoEvento, ...sessao.historicoEventos],
  }
}

/**
 * "Mostrar à turma": a leitura mais recente deste exame passa a aparecer
 * (ou deixa de aparecer) na projeção. Separado de registrar a avaliação —
 * o instrutor pode só responder em voz alta.
 */
export function alternarLiberacaoNaTela(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
  idAcao: IdAcaoAvaliacao,
): SessaoAtendimentoTabletop {
  const estadoAtual = assegurarEstadoVitima(sessao, idVitima)
  if (!estadoAtual.acoesReveladas.includes(idAcao)) return sessao
  const liberada = estadoAtual.liberadasNaTela.includes(idAcao)
  return {
    ...sessao,
    estadosVitimas: {
      ...sessao.estadosVitimas,
      [idVitima]: {
        ...estadoAtual,
        liberadasNaTela: liberada
          ? estadoAtual.liberadasNaTela.filter((id) => id !== idAcao)
          : [...estadoAtual.liberadasNaTela, idAcao],
      },
    },
  }
}

export function declararTriagem(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
  prioridade: PrioridadeTriagem,
  justificativa?: string,
): SessaoAtendimentoTabletop {
  const estadoAtual = assegurarEstadoVitima(sessao, idVitima)
  if (!podeClassificarVitima(estadoAtual)) return sessao

  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'triagem',
    objetoVitimaId: idVitima,
    descricao: `Triagem declarada: ${prioridade.toUpperCase()}`,
    detalhes: justificativa,
  }

  return {
    ...sessao,
    estadosVitimas: {
      ...sessao.estadosVitimas,
      [idVitima]: {
        ...estadoAtual,
        triagemDeclarada: prioridade,
        justificativaTriagem: justificativa,
      },
    },
    historicoEventos: [novoEvento, ...sessao.historicoEventos],
  }
}

export function declararRotaExtracao(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
  rota: TipoRotaExtracao,
  justificativa?: string,
): SessaoAtendimentoTabletop {
  const estadoAtual = assegurarEstadoVitima(sessao, idVitima)

  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'rota',
    objetoVitimaId: idVitima,
    descricao: `Rota de extração definida: ${rota}`,
    detalhes: justificativa,
  }

  return {
    ...sessao,
    estadosVitimas: {
      ...sessao.estadosVitimas,
      [idVitima]: {
        ...estadoAtual,
        rotaDeclarada: rota,
        justificativaRota: justificativa,
      },
    },
    historicoEventos: [novoEvento, ...sessao.historicoEventos],
  }
}

export interface ContextoIntervencao {
  /** ficha da vítima: com ela, as regras de efeito são aplicadas */
  readonly ficha?: FichaVitimaAtendimento | null
  /** onde a equipe declarou ter aplicado */
  readonly regiao?: RegiaoAnatomicaVitima
}

/**
 * Registra uma intervenção executada e aplica as regras da ficha. O efeito
 * vai para o problema (não para os sinais): a equipe só vê o resultado
 * quando reavaliar. Sem regra cadastrada, o efeito fica "a definir".
 */
export function registrarIntervencao(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
  tipo: string,
  rotulo: string,
  parametros: Readonly<Record<string, string | number | boolean>>,
  justificativa?: string,
  contexto: ContextoIntervencao = {},
): SessaoAtendimentoTabletop {
  const estadoAtual = assegurarEstadoVitima(sessao, idVitima)
  const efeitos = contexto.ficha ? avaliarIntervencao(contexto.ficha, estadoAtual, tipo, contexto.regiao) : []

  const novaIntervencao: RegistroIntervencaoAtendimento = {
    id: novoId(),
    tipo,
    rotulo,
    parametros,
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    justificativa,
    ...(contexto.regiao ? { regiao: contexto.regiao } : {}),
    efeitos,
  }

  const problemas = { ...estadoAtual.problemas }
  for (const efeito of efeitos) problemas[efeito.idProblema] = efeito.estadoDepois

  const parametrosFormatados = Object.entries(parametros)
    .map(([chave, valor]) => `${chave}: ${valor}`)
    .join(' · ')
  const efeitosFormatados = efeitos
    .map((e) => `${e.rotuloProblema}: ${ROTULOS_RESULTADO_EFEITO[e.resultado]}`)
    .join(' · ')

  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'intervencao',
    objetoVitimaId: idVitima,
    descricao: `Intervenção: ${rotulo}${contexto.regiao ? ` (${ROTULOS_REGIAO[contexto.regiao].toLowerCase()})` : ''}`,
    detalhes: [
      parametrosFormatados,
      justificativa ? `Justificativa: ${justificativa}` : '',
      efeitosFormatados ? `Efeito: ${efeitosFormatados}` : '',
    ]
      .filter(Boolean)
      .join(' | '),
  }

  return {
    ...sessao,
    estadosVitimas: {
      ...sessao.estadosVitimas,
      [idVitima]: {
        ...estadoAtual,
        problemas,
        intervencoesRealizadas: [...estadoAtual.intervencoesRealizadas, novaIntervencao],
      },
    },
    historicoEventos: [novoEvento, ...sessao.historicoEventos],
  }
}

export const ROTULOS_MOTIVO_AJUSTE: Readonly<Record<MotivoAjusteManual, string>> = {
  execucao_inadequada: 'Execução inadequada',
  efeito_nao_previsto: 'Efeito não previsto na ficha',
  decisao_do_instrutor: 'Decisão do instrutor',
  correcao_de_registro: 'Correção de registro',
}

/** Ordem em que os motivos aparecem na lista (o mais comum primeiro). */
export const MOTIVOS_AJUSTE_MANUAL: readonly MotivoAjusteManual[] = [
  'decisao_do_instrutor',
  'execucao_inadequada',
  'efeito_nao_previsto',
  'correcao_de_registro',
]

/** Ajuste manual do estado de um problema, com motivo escolhido numa lista — fica registrado. */
export function ajustarProblemaManualmente(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
  idProblema: string,
  rotuloProblema: string,
  novoEstado: EstadoProblemaClinico,
  motivo: MotivoAjusteManual,
): SessaoAtendimentoTabletop {
  const estadoAtual = assegurarEstadoVitima(sessao, idVitima)
  const antes = estadoAtual.problemas[idProblema] ?? 'ativo'
  if (antes === novoEstado) return sessao

  const ajuste: AjusteManualProblema = {
    id: novoId(),
    idProblema,
    estadoAntes: antes,
    estadoDepois: novoEstado,
    motivo,
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
  }

  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'ajuste',
    objetoVitimaId: idVitima,
    descricao: `Ajuste manual — ${rotuloProblema}: ${ROTULOS_ESTADO_PROBLEMA[antes]} → ${ROTULOS_ESTADO_PROBLEMA[novoEstado]}`,
    detalhes: `Motivo: ${ROTULOS_MOTIVO_AJUSTE[motivo]}`,
  }

  return {
    ...sessao,
    estadosVitimas: {
      ...sessao.estadosVitimas,
      [idVitima]: {
        ...estadoAtual,
        problemas: { ...estadoAtual.problemas, [idProblema]: novoEstado },
        ajustesManuais: [...estadoAtual.ajustesManuais, ajuste],
      },
    },
    historicoEventos: [novoEvento, ...sessao.historicoEventos],
  }
}

/** Marco da ocorrência (acionamento, deslocamento, chegada, atualização da central) com um toque. */
export function registrarMarco(sessao: SessaoAtendimentoTabletop, tipo: TipoMarcoOcorrencia): SessaoAtendimentoTabletop {
  if (MARCOS_UNICOS.includes(tipo) && sessao.marcos.some((m) => m.tipo === tipo)) return sessao

  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'marco',
    objetoVitimaId: '',
    descricao: ROTULOS_MARCO[tipo],
  }

  return {
    ...sessao,
    marcos: [...sessao.marcos, { id: novoId(), tipo, tempoSimuladoSegundos: sessao.tempoSimuladoSegundos }],
    historicoEventos: [novoEvento, ...sessao.historicoEventos],
  }
}

/**
 * Move a vítima para outro ponto da evolução clínica — `'inicial'`, `'tardio'`
 * (fichas antigas) ou o id de um estágio de `FichaVitimaAtendimento.estagios`.
 * A leitura é a que existir na ficha para esse estágio: nenhuma leitura já
 * registrada muda sozinha — a equipe só percebe ao reavaliar.
 */
export function avancarEstadoVitima(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
  novoEstado: string,
  rotuloEstado?: string,
): SessaoAtendimentoTabletop {
  const estadoAtual = assegurarEstadoVitima(sessao, idVitima)
  if (estadoAtual.estadoEvolucao === novoEstado) return sessao

  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'evolucao',
    objetoVitimaId: idVitima,
    descricao: `Evolução clínica disparada pelo instrutor: Estado ${(rotuloEstado ?? novoEstado).toUpperCase()}`,
  }

  return {
    ...sessao,
    estadosVitimas: {
      ...sessao.estadosVitimas,
      [idVitima]: {
        ...estadoAtual,
        estadoEvolucao: novoEstado,
      },
    },
    historicoEventos: [novoEvento, ...sessao.historicoEventos],
  }
}

export function atualizarTempoSimulado(
  sessao: SessaoAtendimentoTabletop,
  novoTempo: number,
): SessaoAtendimentoTabletop {
  return {
    ...sessao,
    tempoSimuladoSegundos: Math.max(0, novoTempo),
  }
}

export function alternarCronometro(
  sessao: SessaoAtendimentoTabletop,
  rodando?: boolean,
): SessaoAtendimentoTabletop {
  return {
    ...sessao,
    cronometroRodando: rodando ?? !sessao.cronometroRodando,
  }
}

export function alternarModoExibicao(
  sessao: SessaoAtendimentoTabletop,
  modo?: 'aluno' | 'instrutor',
): SessaoAtendimentoTabletop {
  return {
    ...sessao,
    modoExibicao: modo ?? (sessao.modoExibicao === 'aluno' ? 'instrutor' : 'aluno'),
  }
}

export function definirFocoVitima(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string | null,
): SessaoAtendimentoTabletop {
  return {
    ...sessao,
    focoVitimaId: idVitima,
  }
}

export function limparDestaqueRegiao(sessao: SessaoAtendimentoTabletop): SessaoAtendimentoTabletop {
  return {
    ...sessao,
    destaqueRegiaoAnatomica: null,
  }
}

export function definirFase(
  sessao: SessaoAtendimentoTabletop,
  fase: FaseAtendimento,
): SessaoAtendimentoTabletop {
  if (sessao.fase === fase) return sessao

  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'fase',
    objetoVitimaId: sessao.focoVitimaId ?? '',
    descricao: `Fase da ocorrência: ${FASES_ROTEIRO[fase]?.rotulo ?? fase}`,
  }

  return {
    ...sessao,
    fase,
    historicoEventos: [novoEvento, ...sessao.historicoEventos],
  }
}

export function definirNivel(
  sessao: SessaoAtendimentoTabletop,
  nivel: NivelAtendimento,
): SessaoAtendimentoTabletop {
  return {
    ...sessao,
    nivel,
  }
}

export function registrarInterpretacao(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
  letra: LetraXabcde,
  interpretacao: InterpretacaoLetra,
): SessaoAtendimentoTabletop {
  const estadoAtual = assegurarEstadoVitima(sessao, idVitima)

  const ROTULOS_INTERPRETACAO: Record<InterpretacaoLetra, string> = {
    sem_alteracao: 'sem alteração',
    alterado: 'alterado',
    ameaca_vida: 'ameaça à vida',
  }

  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'interpretacao',
    objetoVitimaId: idVitima,
    descricao: `Interpretação da equipe para ${letra}: ${ROTULOS_INTERPRETACAO[interpretacao]}`,
  }

  return {
    ...sessao,
    estadosVitimas: {
      ...sessao.estadosVitimas,
      [idVitima]: {
        ...estadoAtual,
        interpretacoes: {
          ...estadoAtual.interpretacoes,
          [letra]: interpretacao,
        },
      },
    },
    historicoEventos: [novoEvento, ...sessao.historicoEventos],
  }
}

/**
 * Marca (ou desmarca) uma vítima como encontrada pela guarnição. Abrir a ficha
 * no painel do instrutor não conta: só esta ação explícita libera o cartão da
 * vítima na projeção.
 */
export function definirVitimaIdentificada(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
  identificada: boolean,
  nomeVitima?: string,
): SessaoAtendimentoTabletop {
  const jaIdentificada = sessao.vitimasIdentificadas.includes(idVitima)
  if (jaIdentificada === identificada) return sessao

  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'identificacao',
    objetoVitimaId: idVitima,
    descricao: identificada
      ? `Vítima identificada pela guarnição${nomeVitima ? `: ${nomeVitima}` : ''}`
      : `Identificação desfeita pelo instrutor${nomeVitima ? `: ${nomeVitima}` : ''}`,
  }

  return {
    ...sessao,
    vitimasIdentificadas: identificada
      ? [...sessao.vitimasIdentificadas, idVitima]
      : sessao.vitimasIdentificadas.filter((id) => id !== idVitima),
    historicoEventos: [novoEvento, ...sessao.historicoEventos],
  }
}

/** Atualiza a leitura de cena 360° declarada pela guarnição. */
export function atualizarAvaliacaoCena(
  sessao: SessaoAtendimentoTabletop,
  alteracoes: Partial<AvaliacaoCenaTabletop>,
): SessaoAtendimentoTabletop {
  return {
    ...sessao,
    avaliacaoCena: {
      seguranca: 'nao_avaliada',
      quantidadeVitimas: null,
      vitimasEjetadas: null,
      itensVerificados: [],
      ...sessao.avaliacaoCena,
      ...alteracoes,
    },
  }
}

/** Mantém a condição de aprisionamento distinta da gravidade fisiológica. */
export function definirGrauEncarceramento(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
  grau: GrauEncarceramento,
): SessaoAtendimentoTabletop {
  const estadoAtual = assegurarEstadoVitima(sessao, idVitima)
  if (estadoAtual.grauEncarceramento === grau) return sessao
  const rotulos: Record<GrauEncarceramento, string> = {
    nao_avaliado: 'ainda não avaliado',
    sem_encarceramento: 'sem encarceramento', mecanico: 'mecânico', tf1: 'TF1', tf2: 'TF2',
  }
  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(), tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'encarceramento', objetoVitimaId: idVitima,
    descricao: `Encarceramento observado: ${rotulos[grau]}`,
  }
  return {
    ...sessao,
    estadosVitimas: { ...sessao.estadosVitimas, [idVitima]: { ...estadoAtual, grauEncarceramento: grau } },
    historicoEventos: [novoEvento, ...sessao.historicoEventos],
  }
}

/** Registra a troca para luvas de APH antes da extração. */
export function definirTrocaLuvasAPH(sessao: SessaoAtendimentoTabletop): SessaoAtendimentoTabletop {
  if (sessao.luvasAPHConfirmadas) return sessao
  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(), tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'equipe', objetoVitimaId: '', descricao: 'Equipe trocou luvas de vaqueta por luvas de APH',
  }
  return { ...sessao, luvasAPHConfirmadas: true, historicoEventos: [novoEvento, ...sessao.historicoEventos] }
}

/** Registra uma ação de APH 1/APH 2 ou da equipe, sem duplicar o mesmo registro. */
export function registrarAcaoEquipe(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
  tipo: AcaoEquipeAtendimento,
  rotulo: string,
): SessaoAtendimentoTabletop {
  const estadoAtual = assegurarEstadoVitima(sessao, idVitima)
  if (estadoAtual.acoesEquipe?.some((acao) => acao.tipo === tipo)) return sessao
  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(), tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'equipe', objetoVitimaId: idVitima, descricao: rotulo,
  }
  return {
    ...sessao,
    estadosVitimas: {
      ...sessao.estadosVitimas,
      [idVitima]: {
        ...estadoAtual,
        acoesEquipe: [...(estadoAtual.acoesEquipe ?? []), { tipo, tempoSimuladoSegundos: sessao.tempoSimuladoSegundos }],
      },
    },
    historicoEventos: [novoEvento, ...sessao.historicoEventos],
  }
}

/** Resposta livre do instrutor a uma pergunta da turma que não está roteirizada em nenhuma ficha. */
export function registrarAnotacaoMestre(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
  anotacao: string,
): SessaoAtendimentoTabletop {
  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'anotacao',
    objetoVitimaId: idVitima,
    descricao: 'Anotação do instrutor',
    detalhes: anotacao,
  }

  return {
    ...sessao,
    historicoEventos: [novoEvento, ...sessao.historicoEventos],
  }
}

/** Passagem de caso feita pela equipe (ex.: ao receber o SAV), guardada na linha do tempo como foi dita. */
export function registrarPassagemDeCaso(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
  texto: string,
): SessaoAtendimentoTabletop {
  const conteudo = texto.trim()
  if (!conteudo) return sessao
  const novoEvento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'anotacao',
    objetoVitimaId: idVitima,
    descricao: 'Passagem de caso da equipe',
    detalhes: conteudo,
  }
  return { ...sessao, historicoEventos: [novoEvento, ...sessao.historicoEventos] }
}

const ORDEM_LETRAS_XABCDE: readonly LetraXabcde[] = ['X', 'A', 'B', 'C', 'D', 'E']

export interface AnaliseSequenciaXabcde {
  /** Letras examinadas, na ordem em que a equipe pediu o primeiro exame de cada uma. */
  readonly ordemExaminada: readonly LetraXabcde[]
  /** Letras examinadas antes de uma anterior no protocolo ainda não ter sido tocada. */
  readonly foraDeSequencia: ReadonlyArray<{ readonly letra: LetraXabcde; readonly esperadaAntes: LetraXabcde }>
}

/**
 * A partir das ações já reveladas de uma vítima (na ordem em que foram pedidas),
 * reconstrói a ordem em que a equipe percorreu o XABCDE e aponta letras
 * examinadas fora de sequência — dado principal do nível Desafio no debriefing.
 */
export function analisarSequenciaXabcde(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
): AnaliseSequenciaXabcde {
  const estado = sessao.estadosVitimas[idVitima]
  if (!estado) return { ordemExaminada: [], foraDeSequencia: [] }

  const tocadas = new Set<LetraXabcde>()
  const ordemExaminada: LetraXabcde[] = []
  const foraDeSequencia: Array<{ letra: LetraXabcde; esperadaAntes: LetraXabcde }> = []

  for (const idAcao of estado.acoesReveladas) {
    const etapa = ACOES_POR_ID[idAcao]?.etapa
    if (!(ORDEM_LETRAS_XABCDE as readonly string[]).includes(etapa)) continue
    const letra = etapa as LetraXabcde
    if (tocadas.has(letra)) continue

    tocadas.add(letra)
    ordemExaminada.push(letra)

    const indiceLetra = ORDEM_LETRAS_XABCDE.indexOf(letra)
    const anteriorPendente = ORDEM_LETRAS_XABCDE.slice(0, indiceLetra).find((l) => !tocadas.has(l))
    if (anteriorPendente) {
      foraDeSequencia.push({ letra, esperadaAntes: anteriorPendente })
    }
  }

  return { ordemExaminada, foraDeSequencia }
}

/**
 * Lê a sessão como a turma a veria: força o modo aluno, mesmo que o
 * instrutor esteja com o modo instrutor aberto na própria tela. Não é o que
 * vai para a projeção — isso é `montarEstadoPublico` (`projecaoPublica.ts`),
 * que monta uma lista explícita de campos permitidos.
 */
export function sanitizarSessaoParaTurma(sessao: SessaoAtendimentoTabletop): SessaoAtendimentoTabletop {
  if (sessao.modoExibicao === 'aluno') return sessao
  return {
    ...sessao,
    modoExibicao: 'aluno',
  }
}
